import { useQueryClient } from '@tanstack/react-query'
import { uniqBy } from 'lodash-es'
import { Square } from 'lucide-react'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { TextareaWithSubmit } from '@/components/composer/TextareaWithSubmit'
import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { Delay } from '@/components/ui/Delay'
import { FadeSlideAnimation } from '@/components/ui/FadeSlideAnimation'
import { FileDropOverlay } from '@/components/ui/FileDropOverlay'
import { Label } from '@/components/ui/label'
import { SkeletonRows } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { useAgentApi } from '../../../../../components/AgentChat/AgentKindProvider'
import { PageContextSection } from '../../../../../components/AgentChat/PageContextSection'
import type { PreviewDocument } from '../../../../../components/AgentChat/previewDocument'
import type { HandedOverPrompt } from '../../../../../components/AgentChat/useAgentPanelState'
import { ChatMarkdown } from '../../../../../components/Chat/ChatMarkdown'
import { TypingIndicator } from '../../../../../components/Chat/TypingIndicator'
import {
  PageContextEntity,
  type PageContextValue,
  toPageContextPayload,
} from '../../../../../components/InternalAgent/pageContext'
import {
  useAgentConversation,
  useChatItems,
  useDecidePendingAction,
  usePendingActions,
  useRejectAllPendingActions,
  useResumeActiveTurn,
  useSendAgentMessage,
  useStopTurn,
} from '../../../../../hooks/useAgentConversation'
import { useTypewriter } from '../../../../../hooks/useTypewriter'
import { getStoredAutoAccept, setStoredAutoAccept } from '../../../../../lib/agentAutoAccept'
import { apiErrorDetail } from '../../../../../lib/apiError'
import { CacheKeys } from '../../../../../lib/cache'
import { cn } from '../../../../../lib/utils'
import {
  AgentConversationChannel,
  ChatItem,
  ChatItemAttachment,
  ChatToolCall,
  decisionRefusalReason,
  SseEvent,
} from '../../../../../models/agentConversation'
import { ChatMessageAttachments } from './ChatMessageAttachments'
import { ConfirmationCard, PendingConfirmation } from './ConfirmationCard'
import { EmptyState } from './EmptyState'
import { StaffAnswerFeedback } from './StaffAnswerFeedback'
import { ThinkingPanel } from './ThinkingPanel'
import { ToolCallCard } from './ToolCallCard'

// Tools that change a policy change request, or the roster drafts collect, so its pages refetch.
const POLICY_CHANGE_TOOL = /policy_change|^(add|update|remove)_(truck|trailer|driver)$/

interface Props {
  conversationId: string | undefined
  /**
   * The page the user is on, sent with each message. Only the side panels pass
   * this — the standalone page and the lead/customer tabs are already scoped
   * by the conversation itself.
   */
  pageContext?: PageContextValue | null
  /** Drop the header and the card around the thread — the side panel is already one. */
  chromeless?: boolean
  /**
   * A message typed somewhere else (a page's inline composer) to send as soon
   * as this thread is ready. `onHandedOverPromptSent` fires first, so the
   * caller clears it before the send and it can't be submitted twice.
   */
  handedOverPrompt?: HandedOverPrompt
  onHandedOverPromptSent?: () => void
  /**
   * Opt-in, side panel only: every form the agent has filled in this thread, in
   * the order it filled them, for the pane beside the chat. Empty on a
   * conversation switch. Passing this is what turns the pane on.
   */
  onFilledDocuments?: (docs: PreviewDocument[]) => void
  /** Adds one document to that pane — an attachment chip the user clicked. */
  onPreviewDocument?: (doc: PreviewDocument) => void
  /**
   * Widen the file drop zone to an element the caller owns — the side panel
   * hands its whole aside, so a drop anywhere in it lands here. The caller
   * draws the outline itself, over everything it owns.
   */
  dropZoneRef?: React.RefObject<HTMLElement | null>
  /** Fires on drag enter/leave so that wider drop zone can highlight itself. */
  onDraggingChange?: (dragging: boolean) => void
}

// Streaming-only tool state that lives only while a turn is in flight — reset
// to EMPTY when the turn completes (the persisted ChatItems take over, which
// carry the same cards in `content.toolCalls`). The revealed assistant text is
// owned by the useTypewriter hook, not this state. `pending` is intentionally
// NOT part of this: a pending action survives the turn-end reset because the
// user still needs to decide on it.
interface StreamingState {
  activeTools: { toolCallId: string; tool: string; label: string; argsSummary: string; icon: string }[]
  completedToolCalls: ChatToolCall[]
}

const EMPTY_STREAMING: StreamingState = {
  activeTools: [],
  completedToolCalls: [],
}

export function ChatPanel({
  conversationId,
  pageContext,
  chromeless,
  handedOverPrompt,
  onHandedOverPromptSent,
  onFilledDocuments,
  onPreviewDocument,
  dropZoneRef: externalDropZoneRef,
  onDraggingChange,
}: Props) {
  const queryClient = useQueryClient()
  const { kind: agentKind, confirmsWrites } = useAgentApi()
  const { conversation, isLoading: convLoading } = useAgentConversation(conversationId)
  // Lead chats are shared read-only with other staff; only the owner can act
  // on a pending confirmation. The pending-actions endpoint mints HMAC decision
  // tokens, so only the owner polls it (it's owner-only server-side and would
  // 404 for everyone else anyway).
  const isOwner = conversation?.isOwner ?? false
  const { items, isLoading: itemsLoading, refetch: refetchItems } = useChatItems(conversationId)
  // Server-side rehydration of pending confirmations so the card survives
  // navigation away/back. React state alone would be wiped on remount.
  const { pendingActions: serverPending, refetch: refetchPending } = usePendingActions(
    isOwner && confirmsWrites ? conversationId : undefined
  )
  // Per-conversation drafts — switching conversations should restore whatever
  // text the user had typed there, not bleed the current draft across.
  const [draftsByConversation, setDraftsByConversation] = useState<Record<string, string>>({})
  const draft = conversationId ? (draftsByConversation[conversationId] ?? '') : ''
  const setDraft = (text: string) => {
    if (!conversationId) return
    setDraftsByConversation((prev) => ({ ...prev, [conversationId]: text }))
  }
  const [streaming, setStreaming] = useState<StreamingState>(EMPTY_STREAMING)
  // Opus 5 thinks before it writes; without this the panel sits empty for it.
  const [thinkingText, setThinkingText] = useState('')
  // Closing the SSE response stops reading; only Stop cancels the worker turn.
  const abortRef = useRef<AbortController | null>(null)
  // Sticky across conversations and reloads; flipping it back every thread was busywork.
  const [autoAccept, setAutoAccept] = useState(() => getStoredAutoAccept(agentKind))
  const changeAutoAccept = useCallback(
    (on: boolean) => {
      setAutoAccept(on)
      setStoredAutoAccept(agentKind, on)
    },
    [agentKind]
  )
  // Dismissing the context chip is per-record, not just per-path: a
  // drawer-opened record (`?lead=`) leaves the path unchanged as the user
  // clicks through the board.
  const [dismissedContext, setDismissedContext] = useState<string | null>(null)
  const contextKey = pageContext ? `${pageContext.path}|${pageContext.entityId ?? ''}` : null
  const contextIncluded = Boolean(pageContext) && dismissedContext !== contextKey
  // Uploads made from a customer's page belong to that customer, not to Cai Labs,
  // so they reach its documents tab and a filled copy can link back to its records.
  // Not gated on `contextIncluded`: that chip governs what the agent is told,
  // whereas where a document is filed governs whether anyone finds it later.
  const uploadOrganisationId =
    (pageContext?.entityType === PageContextEntity.ORGANISATION ? pageContext.entityId : undefined) ??
    (conversation?.organisationId || undefined)
  // Smooth character-by-character reveal of the streamed assistant reply. The
  // hook owns the visible text; pass `enabled: false` here to turn it off (text
  // then appears instantly) or reuse it wherever streamed text is rendered.
  const {
    text: assistantText,
    isRevealing,
    push: pushReveal,
    reset: resetReveal,
    finish: finishReveal,
  } = useTypewriter()
  // `pending` mirrors the server's PendingAgentAction rows for this
  // conversation, kept in local state so SSE events can push new entries
  // before the next refetch lands.
  const [pending, setPending] = useState<PendingConfirmation[]>([])
  // A crashed turn persists no assistant ChatItem, so without this the whole
  // reply silently vanishes and the user just sees their own message. Tagged
  // with its own conversation so a late error can't surface in another one —
  // which is also why switching conversations must NOT clear it.
  const [streamError, setStreamError] = useState<{
    conversationId: string
    message: string
    // Overrides the banner heading when the turn never started at all.
    title?: string
  } | null>(null)

  // Whenever the server's pending list changes (initial load, decision
  // settled, conversation switch), reseed local state. Keyed by the
  // serialized actionTokens so we don't reseed for object-identity churn.
  // Plus `conversationId`: a remount onto a cached queue leaves the key unchanged.
  const serverPendingKey = serverPending.map((p) => p.actionToken).join('|')
  useEffect(() => {
    setPending(serverPending)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, serverPendingKey])

  // Keep the *last* pending around for the exit animation. While `pending[0]`
  // is undefined, the Delay wrapper continues to render `lastPending` for the
  // height/fade-out transition.
  const [lastPending, setLastPending] = useState<PendingConfirmation | null>(null)
  useEffect(() => {
    if (pending[0]) setLastPending(pending[0])
  }, [pending])

  // useMutation gives us `variables` + `isPending` for free — we can render
  // the just-sent message as an optimistic ChatItem (matching the
  // InsuranceApplication pattern in this codebase). The backend persists the
  // user message at turn start, but we don't see it via refetchItems() until
  // the stream finishes; without optimistic rendering the user's bubble lags
  // behind the agent's first tokens.
  const sendMutation = useSendAgentMessage()
  const decideMutation = useDecidePendingAction()
  const rejectAllMutation = useRejectAllPendingActions()
  const resumeMutation = useResumeActiveTurn()
  const stopMutation = useStopTurn()
  // The turn the worker is running, so Stop knows what to ask it to abandon.
  const [activeTurnId, setActiveTurnId] = useState<string | null>(null)
  // Scope "sending" to the conversation that actually triggered the turn.
  // ChatPanel is one component instance reused across conversations, so an
  // unscoped flag would disable the composer in *every* conversation while a
  // turn streams — switching away from a streaming conversation should still
  // let you type in another one.
  const isSending =
    (sendMutation.isPending && sendMutation.variables.conversationId === conversationId) ||
    (decideMutation.isPending && decideMutation.variables.conversationId === conversationId) ||
    (rejectAllMutation.isPending && rejectAllMutation.variables.conversationId === conversationId) ||
    (resumeMutation.isPending && resumeMutation.variables.conversationId === conversationId)

  // Optimistic-bubble list mirroring the customer-facing chat's pattern.
  // Build a one-element pending list from the in-flight send (gated on
  // conversationId so a Conv-A send doesn't ghost into Conv B), then merge
  // with items via uniqBy keyed by `clientId || id`. When the backend's
  // turn-start persistence surfaces via items refetch, the persisted item
  // shares the same clientId as the pending entry and uniqBy keeps the
  // first occurrence (items, which has the real id) — no dup, no heuristic.
  const pendingMessages: ChatItem[] = useMemo(() => {
    if (!sendMutation.isPending) return []
    if (sendMutation.variables.conversationId !== conversationId) return []
    return [
      {
        id: '',
        clientId: sendMutation.variables.clientId,
        isFromUser: true,
        content: {
          text: sendMutation.variables.text,
          documentIds: sendMutation.variables.documentIds,
          attachments: sendMutation.variables.attachments,
        },
        dateCreated: new Date().toISOString(),
      },
    ]
  }, [sendMutation.isPending, sendMutation.variables, conversationId])

  // Snapshot of the assistant message ids present when the current turn
  // started. While this turn's live output is still on screen, any assistant
  // message persisted *this* turn — i.e. an id not in this snapshot — is
  // hidden from the list so the persisted copy doesn't render on top of it.
  // It reappears the instant the streaming buffer is handed off.
  const preTurnItemIdsRef = useRef<Set<string>>(new Set())

  // The live output is the revealed text AND the streamed tool cards — both
  // are duplicated by the persisted item (`content.toolCalls` + `content.text`)
  // once the items refetch lands, which happens before the handoff. The test
  // is `assistantText`, not `isRevealing`: a short reply finishes revealing
  // while the buffer is still on screen, and only `finishReveal` clears it.
  const hasLiveTurnOutput =
    Boolean(assistantText) ||
    isRevealing ||
    streaming.completedToolCalls.length > 0 ||
    streaming.activeTools.length > 0

  const mergedItems = useMemo(() => {
    const merged = uniqBy([...items, ...pendingMessages], (item) => item.clientId || item.id)
    if (!hasLiveTurnOutput) return merged
    const preTurn = preTurnItemIdsRef.current
    return merged.filter((item) => item.isFromUser || preTurn.has(item.id))
  }, [items, pendingMessages, hasLiveTurnOutput])

  // Reattach to a turn already in flight — the whole point of running it on the
  // worker. Replays from seq 0, so the answer so far reappears before it
  // continues live. Runs once per conversation.
  const sseRef = useRef<(event: SseEvent) => void>(() => {})
  const resumedRef = useRef<string | null>(null)
  // Read inside the stream callback, which outlives the render that opened it.
  const currentConversationRef = useRef<string | undefined>(conversationId)
  currentConversationRef.current = conversationId
  // A stream outlives the conversation that opened it, and ChatPanel is one
  // instance whose streaming buffer belongs to whatever thread is on screen —
  // so an unguarded frame from the thread you just left renders into this one.
  const stillViewing = (id: string) => currentConversationRef.current === id
  useEffect(() => {
    // Waiting for items is not just tidiness: the snapshot below decides which
    // persisted messages stay visible, and an empty one hides the whole thread.
    if (!conversationId || !isOwner || itemsLoading) return
    if (resumedRef.current === conversationId) return
    resumedRef.current = conversationId
    // Everything already on screen predates this turn, so the replay is the
    // only live output and nothing else gets filtered out behind it.
    preTurnItemIdsRef.current = new Set(items.map((item) => item.id))
    const controller = new AbortController()
    resumeMutation.mutate(
      {
        conversationId,
        abortSignal: controller.signal,
        onTurnStarted: setActiveTurnId,
        onEvent: (event) => {
          if (event.type === 'done') return
          if (!stillViewing(conversationId)) return
          if (event.type === 'error') {
            console.error('agent resume stream error', event)
            setStreamError({
              conversationId,
              message: event.message || 'The agent failed to finish this turn.',
            })
            return
          }
          sseRef.current(event)
        },
      },
      {
        onSettled: () => {
          if (!stillViewing(conversationId)) return
          void refetchItems()
          finishReveal(() => setStreaming(EMPTY_STREAMING))
        },
      }
    )
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, isOwner, itemsLoading])

  // handleDecision lives below the early returns; this hook cannot.
  const decideRef = useRef<(token: string, decision: 'confirm' | 'reject') => void>(() => {})
  // Same reason: `submitMessage` is defined past them.
  const submitRef = useRef<(text: string, documentIds: string[], attachments: ChatItemAttachment[]) => void>(
    () => {}
  )

  // Wait for items too: submitting early snapshots an empty history.
  useEffect(() => {
    if (!handedOverPrompt || !conversationId || convLoading || itemsLoading || isSending) return
    onHandedOverPromptSent?.()
    if (handedOverPrompt.draft) {
      // Into the composer, not the thread: the user finishes it (attaches files) and sends.
      setDraftsByConversation((prev) => ({ ...prev, [conversationId]: handedOverPrompt.text }))
      return
    }
    submitRef.current(handedOverPrompt.text, handedOverPrompt.documentIds, handedOverPrompt.attachments)
  }, [handedOverPrompt, conversationId, convLoading, itemsLoading, isSending, onHandedOverPromptSent])

  // One at a time: each confirmation shrinks `pending`, re-running this for the
  // next sibling. The backend resumes only once every sibling is decided.
  useEffect(() => {
    if (!autoAccept || !isOwner || decideMutation.isPending || pending.length === 0) return
    decideRef.current(pending[0].actionToken, 'confirm')
  }, [autoAccept, isOwner, pending, decideMutation.isPending])

  const scrollRef = useRef<HTMLDivElement>(null)
  const localDropZoneRef = useRef<HTMLDivElement>(null)
  const dropZoneRef = externalDropZoneRef ?? localDropZoneRef
  // Owned here, not in the composer: the whole panel lights up, not just the box.
  const [isDraggingFiles, setIsDraggingFiles] = useState(false)
  const handleDraggingChange = useCallback(
    (dragging: boolean) => {
      setIsDraggingFiles(dragging)
      onDraggingChange?.(dragging)
    },
    [onDraggingChange]
  )

  // Per-conversation reset: one component instance serves every thread, so
  // without this the previous one's reveal and tool cards leak into the next.
  // Not `pending` — the reseed effect above owns that queue.
  useEffect(() => {
    resetReveal()
    setStreaming(EMPTY_STREAMING)
    setThinkingText('')
    setLastPending(null)
    setActiveTurnId(null)
    // Drop the watcher for the thread we just left. Not the work — that is on
    // the worker. Coming back re-opens the stream from seq 0, which replays
    // what was missed; leaving it running would instead give that thread two
    // live streams and duplicate every frame.
    abortRef.current?.abort()
    abortRef.current = null
    onFilledDocuments?.([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, resetReveal])

  // A navigated-away stream would otherwise hold a server slot to completion.
  useEffect(() => () => abortRef.current?.abort(), [])

  const canPreview = Boolean(onFilledDocuments)

  // Every form the agent filled, across persisted history and the turn in
  // flight. A turn that fills two forms reports both, in the order filled.
  const filledDocuments = useMemo<PreviewDocument[]>(() => {
    if (!canPreview) return []
    const calls = [
      ...mergedItems.flatMap((item) => item.content.toolCalls ?? []),
      ...streaming.completedToolCalls,
    ]
    const docs: PreviewDocument[] = []
    const seen = new Set<string>()
    for (const call of calls) {
      const documentId = call.resultData?.documentId
      if (!documentId || seen.has(documentId)) continue
      seen.add(documentId)
      docs.push({ documentId, fileName: call.resultData?.fileName })
    }
    return docs
  }, [canPreview, mergedItems, streaming.completedToolCalls])

  // Keyed on the ids, not the memo's array: that rebuilds on every items
  // refetch, which would reopen a pane the user had closed.
  const filledKey = filledDocuments.map((doc) => doc.documentId).join(',')
  useEffect(() => {
    if (filledKey) onFilledDocuments?.(filledDocuments)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filledKey])

  const previewAttachment = useCallback(
    (attachment: ChatItemAttachment) =>
      onPreviewDocument?.({
        documentId: attachment.documentId,
        attachmentId: attachment.attachmentId,
        fileName: attachment.fileName,
      }),
    [onPreviewDocument]
  )

  // Auto-scroll: smooth scroll to the actual bottom via scrollTo on the
  // scroll container (not scrollIntoView on a marker — that captures the
  // target position at call time and lands short whenever the container
  // grows during the 300ms animation, e.g. on conversation switch with a
  // long thread).
  //
  // Multiple re-snaps cover the staged-reveal animations that grow the DOM
  // AFTER our initial useEffect fires:
  //   - ChatMessage / OptimisticUserMessage: Delay(150) + FadeSlide(150) ≈ 300ms
  //   - TypingIndicator: random 100–600ms initial delay + FadeSlide(150) ≈ up to 750ms
  //
  // Each re-issued smooth scrollTo cancels the in-flight animation and
  // retargets to the latest bottom, so the cascade stays smooth.
  const snapToBottom = useCallback(() => {
    const container = scrollRef.current
    if (!container) return
    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' })
  }, [])

  useEffect(() => {
    snapToBottom()
    const t1 = setTimeout(snapToBottom, 250)
    const t2 = setTimeout(snapToBottom, 600)
    const t3 = setTimeout(snapToBottom, 1000)
    return () => {
      clearTimeout(t1)
      clearTimeout(t2)
      clearTimeout(t3)
    }
  }, [
    conversationId,
    items,
    streaming.completedToolCalls.length,
    streaming.activeTools.length,
    sendMutation.isPending,
    decideMutation.isPending,
    pending.length,
    snapToBottom,
  ])

  // Keep the view pinned to the bottom as the typewriter reveals text. Kept
  // separate from the cascade above so a per-frame character update triggers a
  // single cheap scroll rather than re-arming the timeout cascade every frame.
  useEffect(() => {
    snapToBottom()
  }, [assistantText, snapToBottom])

  // The thinking panel shares the card's height with the scroll area, so it
  // mounting or being expanded shrinks the container with no item or token
  // change to re-trigger the effects above.
  useEffect(() => {
    const container = scrollRef.current
    if (!container) return
    const observer = new ResizeObserver(snapToBottom)
    observer.observe(container)
    return () => observer.disconnect()
  }, [snapToBottom])

  if (!conversationId) return <EmptyState />

  if (convLoading) {
    return (
      <div className={cn('h-full', chromeless ? 'py-4' : 'bg-card rounded-lg border p-4')}>
        <SkeletonRows rows={5} rowClassName="h-16" />
      </div>
    )
  }

  if (!conversation) {
    return (
      <div
        className={cn(
          'text-muted-foreground flex h-full items-center justify-center text-sm',
          !chromeless && 'bg-card rounded-lg border'
        )}
      >
        Conversation not found
      </div>
    )
  }

  const handleSseEvent = (event: SseEvent) => {
    if (event.type === 'token') {
      // Feed the typewriter, which reveals the accumulated text at a steady
      // cadence. Tool cards render in their own fixed slot above the assistant
      // text block, so there's no per-event ordering to force-flush for.
      pushReveal(event.text)
      return
    }
    if (event.type === 'thinking') {
      setThinkingText((prev) => prev + event.text)
      return
    }
    setStreaming((prev) => {
      switch (event.type) {
        case 'tool_call_start':
          return {
            ...prev,
            activeTools: [
              ...prev.activeTools,
              {
                toolCallId: event.tool_call_id,
                tool: event.tool,
                label: event.label,
                argsSummary: event.args_summary,
                icon: event.icon,
              },
            ],
          }
        case 'tool_call_end': {
          // Args only exist on the start event, so carry them over from
          // `activeTools` — that way the live card matches the one persisted
          // on the assistant item. A confirmation-resume executes a call made
          // in an earlier turn and so has no start event at all; the end
          // event's own tool/label cover that case.
          const matching = prev.activeTools.find((t) => t.toolCallId === event.tool_call_id)
          return {
            ...prev,
            activeTools: prev.activeTools.filter((t) => t.toolCallId !== event.tool_call_id),
            completedToolCalls: [
              ...prev.completedToolCalls,
              {
                toolCallId: event.tool_call_id,
                tool: matching?.tool || event.tool,
                label: matching?.label || event.label || event.tool_call_id,
                icon: matching?.icon || event.icon,
                argsSummary: matching?.argsSummary ?? '',
                resultSummary: event.result_summary,
                error: event.error,
                resultData: event.result_data && {
                  documentId: event.result_data.document_id,
                  fileName: event.result_data.file_name,
                },
              },
            ],
          }
        }
        default:
          return prev
      }
    })
    if (event.type === 'tool_call_end' && POLICY_CHANGE_TOOL.test(event.tool)) {
      void queryClient.invalidateQueries({ queryKey: [CacheKeys.POLICY_CHANGES] })
      void queryClient.invalidateQueries({ queryKey: [CacheKeys.INTERNAL_ENDORSEMENTS] })
    }
    if (event.type === 'confirmation_required') {
      setPending((prev) => [
        ...prev,
        {
          actionToken: event.action_token,
          tool: event.tool,
          summary: event.summary,
          payload: event.payload,
          currentState: event.current_state,
          provenance: event.provenance,
          pendingIndex: event.pending_index,
          pendingTotal: event.pending_total,
        },
      ])
    }
  }

  const handleDecision = (actionToken: string, decision: 'confirm' | 'reject') => {
    // Same reveal-hiding snapshot as submit: the resumed turn persists an
    // assistant ChatItem we want hidden until the typewriter catches up.
    preTurnItemIdsRef.current = new Set(items.map((item) => item.id))
    setThinkingText('')
    setStreamError(null)
    // Deliberately not abortable: the backend has already flipped the
    // PendingAgentAction to CONFIRMED, so cancelling could skip an approved write.
    decideMutation.mutate(
      {
        conversationId,
        actionToken,
        decision,
        onTurnStarted: setActiveTurnId,
        onEvent: (event) => {
          if (event.type === 'done') return
          if (!stillViewing(conversationId)) return
          if (event.type === 'error') {
            console.error('agent decision stream error', event)
            setStreamError({
              conversationId,
              message: event.message || 'The agent failed to finish this action.',
            })
            return
          }
          handleSseEvent(event)
        },
      },
      {
        // onSettled drops the card regardless, so a refusal needs saying out loud.
        onError: (error) => {
          if (!stillViewing(conversationId)) return
          setStreamError({
            conversationId,
            title: 'That confirmation didn\u2019t go through.',
            message: decisionRefusalReason(error),
          })
        },
        onSettled: async () => {
          // Clearing the buffer belongs to the thread that filled it; doing it
          // from a stream the user has navigated away from wipes whatever the
          // thread now on screen is streaming.
          if (!stillViewing(conversationId)) return
          // Remove the decided pending immediately for snappy UX; the
          // server refetch below confirms.
          setPending((prev) => prev.filter((p) => p.actionToken !== actionToken))
          try {
            await Promise.all([refetchItems(), refetchPending()])
            queryClient.invalidateQueries({ queryKey: [CacheKeys.AGENT_CONVERSATIONS] })
          } finally {
            // Must run even if a refetch throws: the streaming buffer hides
            // this turn's persisted item until it clears, so a stranded
            // buffer would leave the reply on screen but never handed off.
            // Resumed-turn streaming text is now persisted as a ChatItem — let
            // the typewriter finish, then swap it out so it doesn't double-render.
            finishReveal(() => setStreaming(EMPTY_STREAMING))
          }
        },
      }
    )
  }

  const handleRejectAll = () => {
    // Same reveal-hiding snapshot as handleDecision.
    preTurnItemIdsRef.current = new Set(items.map((item) => item.id))
    setThinkingText('')
    setStreamError(null)
    rejectAllMutation.mutate(
      {
        conversationId,
        onTurnStarted: setActiveTurnId,
        // Fires when the POST lands, before the acknowledgment streams — the
        // cards must not linger for the whole follow-up turn.
        onRejected: (result) => {
          if (!stillViewing(conversationId)) return
          // Leave `lastPending` alone — it feeds the card's exit animation.
          if (result.hadErrors) {
            // Keep the local queue; the refetch on settle reseeds it from the server.
            setStreamError({
              conversationId,
              message:
                'The pending confirmations could not all be processed. Anything still pending is shown below.',
            })
          } else {
            setPending([])
          }
        },
        onEvent: (event) => {
          if (event.type === 'done') return
          if (!stillViewing(conversationId)) return
          if (event.type === 'error') {
            console.error('agent reject-all stream error', event)
            setStreamError({
              conversationId,
              message: event.message || 'The agent failed to finish this action.',
            })
            return
          }
          handleSseEvent(event)
        },
      },
      {
        onSuccess: async () => {
          if (!stillViewing(conversationId)) return
          try {
            await Promise.all([refetchItems(), refetchPending()])
            queryClient.invalidateQueries({ queryKey: [CacheKeys.AGENT_CONVERSATIONS] })
          } finally {
            finishReveal(() => setStreaming(EMPTY_STREAMING))
          }
        },
        onError: () => {
          if (!stillViewing(conversationId)) return
          setStreamError({
            conversationId,
            message: 'Rejecting the pending confirmations failed. Please try again.',
          })
        },
      }
    )
  }

  const handleStop = () => {
    // The run is on the worker, so this asks it to stop rather than cancelling
    // a request. Aborting alone would only stop watching.
    if (activeTurnId && conversationId) {
      stopMutation.mutate({ conversationId, turnId: activeTurnId })
    }
  }

  decideRef.current = handleDecision
  sseRef.current = handleSseEvent

  const submitMessage = (
    text: string,
    documentIds: string[] = [],
    attachments: ChatItemAttachment[] = []
  ) => {
    // Allow empty `text` if files are attached — staff often drop a doc and
    // hit send with no caption.
    if ((!text.trim() && documentIds.length === 0) || isSending || pending.length > 0) return
    setDraft('')
    // Snapshot existing assistant messages so the reply persisted during this
    // turn stays hidden until the typewriter finishes revealing it.
    preTurnItemIdsRef.current = new Set(items.map((item) => item.id))
    resetReveal()
    setStreaming(EMPTY_STREAMING)
    setThinkingText('')
    setStreamError(null)

    const controller = new AbortController()
    abortRef.current = controller
    // Queued = the POST landed, so a later stream failure has not lost the message.
    let queued = false

    sendMutation.mutate(
      {
        conversationId,
        text,
        documentIds,
        attachments,
        clientId: uuidv4(),
        pageContext: contextIncluded && pageContext ? toPageContextPayload(pageContext) : null,
        abortSignal: controller.signal,
        onTurnStarted: (turnId) => {
          queued = true
          setActiveTurnId(turnId)
        },
        onEvent: (event) => {
          if (event.type === 'done') return
          if (!stillViewing(conversationId)) return
          if (event.type === 'error') {
            console.error('agent stream error', event)
            setStreamError({
              conversationId,
              message: event.message || 'The agent failed to answer.',
            })
            return
          }
          handleSseEvent(event)
        },
      },
      {
        // A refused send has to hand the text back and say why, in that conversation.
        onError: (error) => {
          if (queued) return
          setDraft(text)
          setStreamError({
            conversationId,
            title: 'That message wasn’t sent.',
            message: [
              apiErrorDetail(error, 'Please try again.'),
              // The composer owns its upload chips and cleared them on submit.
              documentIds.length > 0 ? 'Re-attach the files and send again.' : '',
            ]
              .filter(Boolean)
              .join(' '),
          })
        },
        onSettled: async () => {
          if (!stillViewing(conversationId)) return
          // Persisted ChatItems + any new PendingAgentAction rows are the
          // source of truth — refetch both and clear the streaming buffer.
          try {
            await Promise.all([refetchItems(), refetchPending()])
            queryClient.invalidateQueries({ queryKey: [CacheKeys.AGENT_CONVERSATIONS] })
          } finally {
            // Must run even if a refetch throws: the streaming buffer hides
            // this turn's persisted item until it clears, so a stranded
            // buffer would leave the reply on screen but never handed off.
            // Let the typewriter finish revealing before swapping the streaming
            // buffer for the persisted item (which stays hidden until then).
            finishReveal(() => setStreaming(EMPTY_STREAMING))
          }
        },
      }
    )
  }

  submitRef.current = submitMessage

  const continuesByEmail = conversation.channel === AgentConversationChannel.EMAIL
  const canWrite = conversation.isOwner && !continuesByEmail

  return (
    <div ref={localDropZoneRef} className="relative flex h-full min-h-0 flex-col">
      {/* One outline over thread and composer alike; a wider drop zone draws its own. */}
      {isDraggingFiles && !externalDropZoneRef && <FileDropOverlay />}
      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col transition-colors',
          !chromeless && 'bg-card rounded-lg border'
        )}
      >
        {!chromeless && (
          <div className="border-b p-4">
            <div className="text-lg font-semibold">{conversation.title}</div>
            {!conversation.isOwner && (
              <div className="text-muted-foreground mt-0.5 text-xs">
                Started by {conversation.createdByName}
              </div>
            )}
          </div>
        )}

        <div
          ref={scrollRef}
          className={cn('min-h-0 flex-1 space-y-3 overflow-y-auto', chromeless ? 'py-4' : 'p-4')}
        >
          {itemsLoading && items.length === 0 ? (
            <SkeletonRows rows={4} rowClassName="h-16" />
          ) : (
            <>
              {/* `mergedItems` = items ⊕ optimistic-pending, deduped by
                  `clientId || id`. When the backend's turn-start
                  persistence surfaces via items refetch, the pending entry
                  drops automatically — no heuristic, no race window. */}
              {mergedItems.map((item) => (
                <ChatMessage
                  key={item.id || item.clientId}
                  item={item}
                  conversationId={conversationId}
                  onPreview={canPreview ? previewAttachment : undefined}
                />
              ))}
              {(streaming.completedToolCalls.length > 0 || streaming.activeTools.length > 0) && (
                <div className="space-y-2">
                  {streaming.completedToolCalls.map((call) => (
                    <ToolCallCard key={call.toolCallId} call={call} />
                  ))}
                  {/* In-flight calls join the same log so the row stays put when its result lands. */}
                  {streaming.activeTools.map((call) => (
                    <ToolCallCard
                      key={call.toolCallId}
                      call={{ ...call, resultSummary: null, error: null }}
                      running
                    />
                  ))}
                </div>
              )}
              {assistantText && (
                <div className="text-sm">
                  <ChatMarkdown content={assistantText} streaming />
                </div>
              )}
              {/* Typing indicator: visible after send, before the first
                  assistant token arrives (or while a tool is running with no
                  prose yet). Hidden once the agent emits text or asks for
                  confirmation. Also gated on the mutation's conversationId
                  so dots from a still-streaming Conv A send don't appear in
                  Conv B after a switch. */}
              <TypingIndicator
                visible={
                  isSending &&
                  sendMutation.variables?.conversationId === conversationId &&
                  !assistantText &&
                  !thinkingText &&
                  streaming.activeTools.length === 0 &&
                  pending.length === 0
                }
              />
              {streamError?.conversationId === conversationId && (
                <Banner variant="error" small onDismiss={() => setStreamError(null)}>
                  <span className="font-medium">
                    {streamError.title ?? <>The agent couldn&rsquo;t finish this turn.</>}
                  </span>{' '}
                  {streamError.message}
                </Banner>
              )}
              {/* Inline confirmation card — replaces the modal so the user
                  can keep scrolling history / switching conversations while
                  pending. Renders at the end of the message list. Animates
                  height + opacity in and out via Delay + FadeSlideAnimation;
                  `lastPending` keeps the data alive through the exit. Only the
                  chat owner can decide; other staff view the chat read-only.
                  Reject all (INS-310) is offered only when it does more than
                  Reject; auto-accept drains the queue itself. */}
              {conversation.isOwner && !autoAccept && (
                <Delay visible={Boolean(pending[0])} initiallyVisible={false} delay={200}>
                  <FadeSlideAnimation>
                    {lastPending && (
                      <ConfirmationCard
                        pending={lastPending}
                        isSubmitting={decideMutation.isPending || rejectAllMutation.isPending}
                        onConfirm={(token) => handleDecision(token, 'confirm')}
                        onReject={(token) => handleDecision(token, 'reject')}
                        onRejectAll={pending.length > 1 ? handleRejectAll : undefined}
                      />
                    )}
                  </FadeSlideAnimation>
                </Delay>
              )}
            </>
          )}
        </div>

        <div className={cn(!chromeless && 'px-4')}>
          <ThinkingPanel text={thinkingText} active={isSending} />
        </div>
      </div>

      <div className="mt-2.5">
        {canWrite && pageContext && (
          <PageContextSection
            context={pageContext}
            included={contextIncluded}
            onToggle={(on) => setDismissedContext(on ? null : contextKey)}
          />
        )}
        {canWrite ? (
          <TextareaWithSubmit
            value={draft}
            onValueChange={setDraft}
            placeholder="Send a message"
            disabled={isSending || pending.length > 0}
            enableUploads
            uploadOrganisationId={uploadOrganisationId}
            dropTargetRef={dropZoneRef}
            onDraggingChange={handleDraggingChange}
            onSubmitMessage={({ message, documentIds, attachments }) =>
              submitMessage(
                message,
                documentIds,
                attachments.map((a) => ({
                  documentId: a.documentId,
                  attachmentId: a.attachmentId,
                  fileName: a.fileName,
                  fileExtension: a.fileExtension,
                  fileSize: a.fileSize,
                  // Without this the just-sent chips stay unclickable all turn.
                  fileUrl: a.fileUrl,
                }))
              )
            }
            buttonProps={{ disabled: isSending || pending.length > 0 }}
            wrapperClassName="mb-[-19px]"
            addonChildren={
              <>
                {confirmsWrites && (
                  <div className="mr-1 flex items-center gap-2">
                    <Switch id="auto-accept" checked={autoAccept} onCheckedChange={changeAutoAccept} />
                    <Label htmlFor="auto-accept" className="text-muted-foreground text-xs font-normal">
                      Auto-accept edits
                    </Label>
                  </div>
                )}
                {isSending && activeTurnId && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleStop}
                    loading={stopMutation.isPending ? 'Stopping…' : undefined}
                  >
                    <Square className="text-error fill-current" />
                    Stop
                  </Button>
                )}
              </>
            }
          />
        ) : (
          <div className="bg-card text-muted-foreground rounded-lg border px-4 py-3 text-center text-sm">
            {continuesByEmail ? (
              'This conversation continues by email.'
            ) : (
              <>Read-only — this chat was started by {conversation.createdByName}.</>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const ChatMessage = memo(function ChatMessage({
  item,
  conversationId,
  onPreview,
}: {
  item: ChatItem
  conversationId: string | undefined
  onPreview?: (attachment: ChatItemAttachment) => void
}) {
  const text = item.content.text ?? ''
  const attachments = item.content.attachments ?? []
  const toolCalls = item.content.toolCalls ?? []
  if (item.isFromUser) {
    const bubble = (
      <div className="ml-auto flex w-fit max-w-[80%] flex-col items-end gap-1.5">
        {attachments.length > 0 && <ChatMessageAttachments attachments={attachments} onPreview={onPreview} />}
        {text && (
          <div className="bg-primary text-primary-foreground rounded-lg px-3 py-2 text-sm whitespace-pre-wrap">
            {text}
          </div>
        )}
      </div>
    )
    // Only newly-sent (optimistic) messages animate in. Persisted messages
    // already carry a server `id`, so they render instantly — otherwise the
    // whole thread re-animates every time the conversation is opened.
    if (item.id) return bubble
    return (
      <Delay visible={true} initiallyVisible={false} delay={150}>
        <FadeSlideAnimation>{bubble}</FadeSlideAnimation>
      </Delay>
    )
  }
  // Tool lines first, then the prose — the same order the turn streamed in.
  // They sit tighter to each other than to the answer they led to.
  return (
    <div className="space-y-3">
      {toolCalls.length > 0 && (
        <div className="space-y-2">
          {toolCalls.map((call) => (
            <ToolCallCard key={call.toolCallId} call={call} />
          ))}
        </div>
      )}
      {attachments.length > 0 && (
        <div className="max-w-[80%]">
          <ChatMessageAttachments attachments={attachments} align="start" onPreview={onPreview} />
        </div>
      )}
      {text && (
        <div className="max-w-[80%] text-sm">
          <ChatMarkdown content={text} />
        </div>
      )}
      {item.canRate && <StaffAnswerFeedback item={item} conversationId={conversationId} />}
    </div>
  )
})
